import { Dialog, DialogContent } from '@material-ui/core'
import PropTypes from 'prop-types'
import React from 'react'

import { fetchAtHomeBookings } from 'api/atHome'

import { bookingRow } from './format'
import AtHomeCrudPage from './shared/AtHomeCrudPage'
import { parentLinkOptions } from './shared/parentLink'

// Same lead as the Bookings tabs (owner, 2026-10-21): when it was booked,
// then who (category, teacher, parent, child), then the session, then
// attendance -- what this list is for -- the two stage marks (server time,
// with how far from the slot start / end), the teacher's notes, the photo,
// whether those two went to the parent, and the fee. The old "Listed for
// class" was the TIME's grade ("Any" on every prod booking, and blank here);
// Child's grade is the class the parent picked while booking.
const columnsFor = (rowsRef, onPhoto) => [
  { name: 'bookedOnLabel', label: 'Booked on', options: { keepOrder: true } },
  { name: 'bookedAtLabel', label: 'Booked at', options: { keepOrder: true } },
  { name: 'categoryName', label: 'Category' },
  { name: 'teacherName', label: 'Teacher' },
  {
    name: 'parentName',
    label: 'Parent',
    options: parentLinkOptions(rowsRef, 'parentName')
  },
  {
    name: 'parentPhone',
    label: "Parent's phone",
    options: parentLinkOptions(rowsRef, 'parentPhone')
  },
  { name: 'childName', label: 'Child' },
  { name: 'childGradeLabel', label: "Child's grade" },
  { name: 'schoolName', label: 'School' },
  { name: 'dateLabel', label: 'Session date', options: { keepOrder: true } },
  { name: 'timeLabel', label: 'Session time', options: { keepOrder: true } },
  { name: 'apartmentName', label: "Apartment (the child's home)" },
  { name: 'attendanceLabel', label: 'Attendance' },
  // Started = when the parent's start code was entered (that one step marks
  // arrival and start together); Session closed = when the teacher ended it.
  // Both are for us and for the teacher's own page: the parent app cannot
  // read either one (it looks for startedAt / completedAt, the API sends
  // started_at / completed_at).
  {
    name: 'startedLabel',
    label: 'Started (start code entered)',
    options: { keepOrder: true }
  },
  {
    name: 'completedLabel',
    label: 'Session closed',
    options: { keepOrder: true }
  },
  {
    name: 'notes',
    label: "Teacher's notes (reach the parent only with a photo)",
    options: { wrap: true }
  },
  {
    name: 'photoUrl',
    label: 'Photo (goes to the parent with the notes)',
    options: {
      // Opens large on this page, not a new tab (owner, 2026-10-22).
      customBodyRender: (url) =>
        url ? (
          <button
            type="button"
            onClick={() => onPhoto(url)}
            title="Open the photo"
            style={{
              padding: 0,
              border: 0,
              background: 'none',
              cursor: 'zoom-in'
            }}
          >
            <img
              src={url}
              alt="Session"
              style={{
                width: 56,
                height: 56,
                objectFit: 'cover',
                borderRadius: 6,
                display: 'block'
              }}
            />
          </button>
        ) : (
          '—'
        )
    }
  },
  { name: 'summaryLabel', label: 'Notes + photo sent to parent' },
  { name: 'paymentLabel', label: 'Fee payment' }
]

/** Attended: sessions marked present, and only those (owner, 2026-10-21).
 * A no-show or a past session nobody marked is not attended: it stays under
 * Bookings, where ops close it. Notes and photo are read-only here (the
 * teacher adds them once, and the parent gets them once). */
const AtHomeAttended = ({ kind = 'trial' }) => {
  // The rows as drawn, for the parent-link cells.
  const rowsRef = React.useRef([])
  const [photo, setPhoto] = React.useState(null)

  const fetchList = React.useCallback(async () => {
    // One kind per tab, filtered by the server (see Bookings.js).
    const list = (await fetchAtHomeBookings({ view: 'attended', kind })).map(
      bookingRow
    )
    rowsRef.current = list
    return list
  }, [kind])

  return (
    <>
      <AtHomeCrudPage
        subtitle="Sessions marked attended."
        refreshKey={kind}
        entityLabel="Session"
        fetchList={fetchList}
        columns={columnsFor(rowsRef, setPhoto)}
      />
      <Dialog
        open={Boolean(photo)}
        onClose={() => setPhoto(null)}
        maxWidth="lg"
      >
        <DialogContent style={{ padding: 8 }} onClick={() => setPhoto(null)}>
          {photo && (
            <img
              src={photo}
              alt="Session"
              style={{
                display: 'block',
                maxWidth: '100%',
                maxHeight: '85vh',
                margin: '0 auto',
                borderRadius: 6
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}

AtHomeAttended.propTypes = { kind: PropTypes.oneOf(['trial', 'regular']) }

export default AtHomeAttended
